import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { Input, Button, Subtitle } from '@panwds/react-ui'
import { DeleteIcon, PlusIcon, FolderIcon } from '@panwds/icons'
import Line from '../../assets/tenant-tree-stroke.svg'
import type { SyntheticEvent, VFC } from 'react'
import type { CommonStoreState } from '../../store'
import type { Actions } from '../../actions'

type TSGNodeType = {
  tsg_name: string
}

type displayInputType = {
  index: number
  isLocked: boolean
}

type UTSGCreateTreeProps = {
  state: CommonStoreState
  actions: Actions
  setRecipientName?: (name: string) => void
  tsgName: string
}

const TSG_LEVEL_LIMIT = 9

const UTSGCreateTree: VFC<UTSGCreateTreeProps> = (props) => {
  const { actions, state, setRecipientName, tsgName } = props
  const { tsgDisplayType } = state
  const { updateActivationData } = actions
  const [mousOverAdd, setMouseOverAdd] = useState<boolean>(false)
  const [tsgData, setTsgData] = useState<TSGNodeType[]>([
    {
      tsg_name: tsgName,
    },
  ])
  // maintain a ref that saves the above input refs
  const tsgDataRefs = useRef<Array<HTMLDivElement | null>>([null])

  const [displayInput, setDisplayInput] = useState<displayInputType>(
    { index: 0, isLocked: false }
  )

  const tsgLevelLimit = useMemo(() => {
    switch (tsgDisplayType) {
      case 'default':
        return TSG_LEVEL_LIMIT
      case 'absolute_root_with_children':
        return TSG_LEVEL_LIMIT
      case 'absolute_root_without_children':
        return 1
      default:
        return TSG_LEVEL_LIMIT
    }
  }, [tsgDisplayType])

  useEffect(() => {
    // if limit change to 1, only keep the first element
    if (tsgLevelLimit === 1) {
      setTsgData((prevState: TSGNodeType[]) => {
        return [prevState[0]]
      })
    }
  }, [tsgLevelLimit])

  useEffect(() => {
    setTsgData([{ tsg_name: props.tsgName }])
  }, [props.tsgName])

  const addNode = useCallback(() => {
    if (tsgData.length === tsgLevelLimit) {
      // cannot exceed max levels
      return
    }
    if (tsgData[tsgData.length - 1].tsg_name === '') {
      return
    }
    setTsgData((prevState: TSGNodeType[]) => {
      setDisplayInput({
        index: prevState.length,
        isLocked: true,
      })
      return [...prevState, { tsg_name: '' }]
    })
    tsgDataRefs.current.push(null)
  }, [tsgLevelLimit, tsgData])

  const removeNode = useCallback((index: number) => {
    if (tsgData.length === 1) {
      return
    }
    setTsgData((prevState: TSGNodeType[]) => {
      const newState = [...prevState]
      newState.splice(index, 1)
      return newState
    })
    const newInputRefs = [...tsgDataRefs.current]
    newInputRefs.splice(index, 1)
    tsgDataRefs.current = newInputRefs
  }, [tsgData.length])

  const PlaceHolder = ({ index }: { index: number }) => {
    const emptyUnit = 16 + (41 * (index - 1))
    if (index === 0) {
      return <></>
    }
    return <div style={{ paddingLeft: `${emptyUnit}px` }}>
      <img style={{ width: '25px', height: '33px' }} src={Line} alt={'line'} />
    </div>
  }

  const AddButton = useMemo(() => {
    const spaceLeft = 16 + 41 * (tsgData.length - 1)
    if (tsgData.length === tsgLevelLimit) {
      return
    }
    return (
      <div
        className='cm-flex'
        style={{ marginLeft: `${spaceLeft}px` }}
      >
        <img style={{ width: '25px', height: '33px' }} src={Line} alt={'line'} />
        <Button
          addClassName='cm-mt-4 cm-pl-1.5'
          aria-label='add-icon'
          icon={<PlusIcon size='sm' className='cm-text-blue-600' />}
          appearance="secondary"
          size='md'
          onClick={addNode}
          onMouseOver={() => setMouseOverAdd(true)}
          onMouseOut={() => setMouseOverAdd(false)}
        >
          {mousOverAdd && 'Allocate to sub-tenant'}
        </Button>
      </div>
    )
  }, [tsgData.length, tsgLevelLimit, addNode, mousOverAdd])

  const tsgNameOnChange = ({ target }: { target: HTMLInputElement }, index: number) => {
    setTsgData((prevState: TSGNodeType[]) => {
      const newState = [...prevState]
      newState[index].tsg_name = target?.value
      return newState
    })
  }

  const onMouseOverNode = (index: number) => {
    setDisplayInput((prevState: displayInputType) => {
      if (!prevState.isLocked) {
        return {
          index,
          isLocked: prevState.isLocked
        }
      }
      return prevState
    })
  }

  const onMouseLeaveNode = (index: number) => {
    setDisplayInput((prevState: displayInputType) => {
      if (!prevState.isLocked) {
        return {
          index: -1,
          isLocked: prevState.isLocked
        }
      }
      return prevState
    })
  }

  const onFocusInput = (index: number) => {
    setDisplayInput({
      index,
      isLocked: true,
    })
  }

  const onBlurInput = useCallback((e: SyntheticEvent, index: number) => {
    if (tsgData[index].tsg_name === '') {
      e.preventDefault()
      const input = (tsgDataRefs.current[index]?.querySelector('span')?.querySelector('input') as HTMLInputElement)
      input.focus()
    }
    else {
      setDisplayInput({
        index: -1,
        isLocked: false,
      })
    }
  }, [tsgData])

  const renderNodes = useMemo(() => {
    return tsgData.map((item: TSGNodeType, index: number) => {
      const isCurrentDisplayInput = Boolean(index === displayInput.index)
      const isLastNode = Boolean(index === (tsgData.length - 1))
      return (
        <div className='cm-flex' key={index}>
          <PlaceHolder index={index} />
          <div
            className='cm-mt-4 cm-flex'
            onMouseOver={() => onMouseOverNode(index)}
            onMouseLeave={() => onMouseLeaveNode(index)}
          >
            <div
              ref={(ref) => tsgDataRefs.current[index] = ref}
            >
              <Input
                addClassName={isCurrentDisplayInput ? '' : 'cm-border-white cm-bg-white'}
                value={item.tsg_name}
                placeholder='New Tenant Name'
                readOnly={!isCurrentDisplayInput}
                onChange={(e) => tsgNameOnChange(e, index)}
                onFocus={() => onFocusInput(index)}
                onBlur={(e) => onBlurInput(e, index)}
                prefix={<FolderIcon size='sm' className='cm-text-gray-600' />}
                suffix={(isLastNode && !isCurrentDisplayInput) ? <Subtitle
                  addClassName='cm-bg-[#EEFBE5] cm-border-solid cm-border-[#CFF0C0] cm-text-[#005E1E] cm-rounded-md cm-border cm-p-1'
                  as='span'
                  size='sm'
                >Recipient</Subtitle> : null}
              />
            </div>
            {isCurrentDisplayInput && <Button
              aria-label='delete-icon'
              icon={<DeleteIcon size='sm' className='cm-text-blue-600' />}
              appearance="clear"
              onClick={() => removeNode(index)}
            />}
          </div>
        </div>
      )
    })
  }, [displayInput.index, onBlurInput, removeNode, tsgData])

  useEffect(() => {
    // for auto focus when add a new TSG
    const input = (tsgDataRefs.current[tsgData.length - 1]?.querySelector('span')?.querySelector('input') as HTMLInputElement)
    input.focus()
  }, [updateActivationData, tsgData.length])

  useEffect(() => {
    if (typeof setRecipientName === 'function') {
      setRecipientName(tsgData[tsgData.length - 1].tsg_name)
    }
    const tsgCreateList = tsgData.map(each => ({ display_name: each.tsg_name }))
    updateActivationData({ tsgCreateList })
  }, [setRecipientName, tsgData, updateActivationData])

  return (
    <div className='cm-max-w-[500px] cm-ml-2 cm-mb-2'>
      {renderNodes}
      {AddButton}
    </div>
  )
}


export default UTSGCreateTree
